import { useRef, useEffect, useCallback } from "react";

/**
 * Custom hook for creating a debounced version of a callback function.
 *
 * Features:
 * - Delays callback execution until after a specified delay period
 * - Cancels previous pending calls when a new one is made
 * - Maintains stable function reference (only changes when delay changes)
 * - Always uses the latest callback via ref (avoids stale closures)
 * - Properly cleans up timeouts on unmount and delay changes
 * - Supports async callbacks
 * - Type-safe with generics
 *
 * @param callback - The function to debounce
 * @param delay - Delay in milliseconds before executing the callback
 * @returns Debounced version of the callback with the same signature
 *
 * @example
 * const debouncedSearch = useDebouncedCallback(
 *   (searchTerm: string) => {
 *     performSearch(searchTerm);
 *   },
 *   300
 * );
 *
 * // Later in event handler:
 * debouncedSearch(inputValue);
 */
//eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300,
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);

  // Update callback ref when it changes to always use the latest version
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup timeout on unmount and when delay changes
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [delay]);

  const debouncedCallback = useCallback(
    ((...args: Parameters<T>) => {
      // Cancel any pending call
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Schedule new call
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
        timeoutRef.current = null;
      }, delay);
    }) as T,
    [delay],
  );

  return debouncedCallback;
}
